import { Component, ElementRef, EventEmitter, forwardRef, HostListener, Input, Output } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

export interface VwSelectOption {
  value: string;
  label: string;
}

// Drop-in replacement for a plain <select formControlName="x"> that adds a
// type-to-filter search box — same formControlName/ngModel binding via
// ControlValueAccessor, just swap the tag and pass [options] instead of
// writing out <option> tags. Built as a generic form-control rather than
// wiring search logic per-field, so it can be rolled into any dropdown
// across the app incrementally without touching that field's own
// component logic.
@Component({
  selector: 'app-searchable-select',
  templateUrl: 'searchable-select.component.html',
  styleUrls: ['searchable-select.component.scss'],
  providers: [{
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => SearchableSelectComponent),
    multi: true
  }]
})
export class SearchableSelectComponent implements ControlValueAccessor {
  @Input() options: VwSelectOption[] = [];
  @Input() placeholder = 'Select...';
  @Input() searchPlaceholder = 'Search...';
  // Named "change" (not e.g. "selectionChange") so a field that previously
  // relied on a native <select>'s (change) handler — like signup's
  // onCountryChange() — keeps working unchanged after swapping the tag.
  @Output() change = new EventEmitter<string>();

  open = false;
  query = '';
  value: string = null;
  disabled = false;

  private onChange: (value: string) => void = () => { };
  private onTouched: () => void = () => { };

  constructor(private elRef: ElementRef<HTMLElement>) { }

  get filteredOptions(): VwSelectOption[] {
    const q = this.query.trim().toLowerCase();
    if (!q) {
      return this.options;
    }
    return this.options.filter(o => o.label.toLowerCase().includes(q));
  }

  get selectedLabel(): string {
    const match = this.options.find(o => o.value === this.value);
    return match ? match.label : '';
  }

  toggle(): void {
    if (this.disabled) {
      return;
    }
    this.open = !this.open;
    if (this.open) {
      this.query = '';
    } else {
      this.onTouched();
    }
  }

  select(option: VwSelectOption): void {
    this.value = option.value;
    this.onChange(this.value);
    this.onTouched();
    this.change.emit(this.value);
    this.open = false;
  }

  @HostListener('document:click', ['$event'])
  onDocClick(event: MouseEvent): void {
    if (!this.elRef.nativeElement.contains(event.target as Node)) {
      this.open = false;
    }
  }

  writeValue(value: string): void {
    this.value = value;
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }
}
